import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import { debounce } from '@chatwoot/utils';

const state = {
  mineCount: 0,
  unAssignedCount: 0,
  allCount: 0,
  // Eltafouk: parallel slot fed by `getUnread` — exact same /meta endpoint
  // but with conversation_type=unread layered onto whatever scope (inbox,
  // team, label) is currently in play. Drives the "غير مقروء" pill badge
  // so it always reflects the same number the tab counters would land on
  // after a click; previously the badge was summed from a different API
  // (inboxes/unattended_counts) which doesn't filter by status=open, and
  // they drifted by 1-2 for non-open-but-unread conversations.
  unreadMineCount: 0,
  unreadUnAssignedCount: 0,
  unreadAllCount: 0,
  // Unread mentions for the current agent — drives the sidebar "Mentions" badge.
  // Independent of inbox/status; fed by the /meta endpoint and bumped in
  // real-time when a `conversation.mentioned` event arrives.
  mentionCount: 0,
  // display_ids of the conversations whose mention the agent hasn't opened yet
  // (from the mentions list response). Lets each row flag an unread mention
  // separately from the conversation's own unread-message state.
  unreadMentionIds: [],
};

export const getters = {
  getStats: $state => $state,
  getMentionCount: $state => $state.mentionCount,
  getUnreadMentionIds: $state => $state.unreadMentionIds,
};

// Create a debounced version of the actual API call function
const fetchMetaData = async (commit, params) => {
  try {
    const response = await ConversationApi.meta(params);
    const {
      data: { meta },
    } = response;
    commit(types.SET_CONV_TAB_META, meta);
  } catch (error) {
    // ignore
  }
};

const fetchUnreadMetaData = async (commit, params) => {
  try {
    const response = await ConversationApi.meta({
      ...params,
      // Eltafouk: getUnread normally forces conversation_type=unread. But when the
      // caller passes an explicit `unread` flag (the all-comments badge scopes via
      // conversation_type='comments' + unread so it doesn't depend on the
      // not-yet-loaded comment-inbox-id list), keep that conversation_type so the
      // comment scope survives — the additive `unread` param does the narrowing.
      conversationType: params.unread ? params.conversationType : 'unread',
    });
    const {
      data: { meta },
    } = response;
    commit(types.SET_CONV_UNREAD_META, meta);
  } catch (error) {
    // ignore
  }
};

// Eltafouk: the sidebar's "Mentions" badge needs only the unread mention
// count, which the /meta endpoint returns regardless of scope. Fetching it via
// the generic `get` action wrote the global mine/unassigned/all counts into the
// SHARED stats store too, clobbering the mention-scoped tab counts the Mentions
// list had set — so on a refresh the tabs reverted to the whole-inbox totals.
// This path commits ONLY mention_count and leaves the tab counters untouched.
const fetchMentionCount = async commit => {
  try {
    const response = await ConversationApi.meta({});
    const {
      data: { meta },
    } = response;
    commit(types.SET_MENTION_COUNT, meta?.mention_count);
  } catch (error) {
    // ignore
  }
};

const debouncedFetchMetaData = debounce(fetchMetaData, 500, false, 1500);
const longDebouncedFetchMetaData = debounce(fetchMetaData, 5000, false, 10000);
const superLongDebouncedFetchMetaData = debounce(
  fetchMetaData,
  10000,
  false,
  20000
);

const debouncedFetchUnreadMetaData = debounce(
  fetchUnreadMetaData,
  500,
  false,
  1500
);

export const actions = {
  get: async ({ commit, state: $state }, params) => {
    if ($state.allCount > 5000) {
      superLongDebouncedFetchMetaData(commit, params);
    } else if ($state.allCount > 100) {
      longDebouncedFetchMetaData(commit, params);
    } else {
      debouncedFetchMetaData(commit, params);
    }
  },
  // Eltafouk: companion to `get`. Always force conversation_type=unread
  // on the params so the badge's count source matches exactly what the
  // backend's unread filter would return — no semantic gap with the tab.
  getUnread: async ({ commit }, params) => {
    debouncedFetchUnreadMetaData(commit, params);
  },
  // Eltafouk: zero the unread badge slot synchronously. The unread* counts are
  // a single account-wide slot shared by every list view (DMs and Comments use
  // the same reused ChatList instance), so on navigation the previous view's
  // number would otherwise render under the new view until the re-scoped
  // `getUnread` (/meta) call resolves (≥500ms debounce) — the cross-section
  // "غير مقروء leaks between التعليقات and المحادثات" report. Clearing here means
  // the badge briefly hides (count 0 → v-if off) instead of showing another
  // view's count; the immediately-dispatched getUnread refills it for THIS view.
  resetUnread: ({ commit }) => {
    commit(types.SET_CONV_UNREAD_META, {
      mine_count: 0,
      unassigned_count: 0,
      all_count: 0,
    });
  },
  set({ commit }, meta) {
    commit(types.SET_CONV_TAB_META, meta);
  },
  incrementMentionCount({ commit }) {
    commit(types.INCREMENT_MENTION_COUNT);
  },
  // Refresh only the sidebar's unread-mention badge without disturbing the
  // tab counters (which are owned by whichever conversation list is open).
  getMentionCount: async ({ commit }) => {
    fetchMentionCount(commit);
  },
  // Drop a conversation from the unread-mention list the moment the agent opens
  // it, so its row marker / unread filter update without waiting for a refetch.
  markMentionRead({ commit }, conversationId) {
    commit(types.REMOVE_UNREAD_MENTION_ID, conversationId);
  },
  // Flag a conversation as an unread mention when one arrives in real-time, so
  // the new row gets its marker / counts without a refetch.
  addUnreadMention({ commit }, conversationId) {
    commit(types.ADD_UNREAD_MENTION_ID, conversationId);
  },
};

export const mutations = {
  [types.SET_CONV_TAB_META](
    $state,
    {
      mine_count: mineCount,
      unassigned_count: unAssignedCount,
      all_count: allCount,
      mention_count: mentionCount,
      unread_mention_ids: unreadMentionIds,
    } = {}
  ) {
    $state.mineCount = mineCount;
    $state.allCount = allCount;
    $state.unAssignedCount = unAssignedCount;
    // Only the /meta endpoint returns mention_count; guard so the index-meta
    // path (which omits it) doesn't clobber the badge count back to undefined.
    if (mentionCount !== undefined) {
      $state.mentionCount = mentionCount;
    }
    // Only the mentions list response carries unread_mention_ids; guard so
    // other views' meta don't wipe it.
    if (unreadMentionIds !== undefined) {
      $state.unreadMentionIds = unreadMentionIds;
    }
    $state.updatedOn = new Date();
  },
  [types.SET_CONV_UNREAD_META](
    $state,
    {
      mine_count: mineCount,
      unassigned_count: unAssignedCount,
      all_count: allCount,
    } = {}
  ) {
    $state.unreadMineCount = mineCount;
    $state.unreadAllCount = allCount;
    $state.unreadUnAssignedCount = unAssignedCount;
  },
  [types.INCREMENT_MENTION_COUNT]($state) {
    $state.mentionCount += 1;
  },
  [types.SET_MENTION_COUNT]($state, mentionCount) {
    if (mentionCount !== undefined) {
      $state.mentionCount = mentionCount;
    }
  },
  [types.REMOVE_UNREAD_MENTION_ID]($state, conversationId) {
    $state.unreadMentionIds = $state.unreadMentionIds.filter(
      id => id !== conversationId
    );
  },
  [types.ADD_UNREAD_MENTION_ID]($state, conversationId) {
    if (!$state.unreadMentionIds.includes(conversationId)) {
      $state.unreadMentionIds = [...$state.unreadMentionIds, conversationId];
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
